import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Popconfirm, Tooltip } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import { getDevicesByUserId, deleteDevice } from '../../services/api';
import toast from 'react-hot-toast';
import { allOptions } from '../utils/deviceTypeOptions';

const Devices = ({ setTitle }) => {
  const [devices, setDevices] = useState([]);
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setTitle('EarthVerse CRM - 用戶設備管理');
    fetchDevices();
  }, [setTitle]);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const fetchedDevices = await getDevicesByUserId(userId);
      setDevices(fetchedDevices);
    } catch (error) {
      console.error('Error fetching Devices:', error);
      toast.error('Failed to fetch devices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = useCallback(() => {
    navigate('/permissions');
  }, [navigate]);

  const handleDeleteDevice = async (deviceId, token) => {
    try {
      await deleteDevice(deviceId, token);
      setDevices(prevDevices => prevDevices.filter(device => device.id !== deviceId));
      toast.success('Device deleted successfully!');
    } catch (error) {
      console.error('Error deleting device:', error);
      toast.error('Failed to delete device. Please try again.');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: (a, b) => a.type.localeCompare(b.Type),
      render: (type) => (
        <>
          {allOptions[type]?.label || type}
        </>
      ),
    },
    {
      title: 'Device Name',
      dataIndex: 'device_name',
      key: 'device_name',
      width: 200, // 固定寬度
      ellipsis: true, // 自動省略超出部分
      sorter: (a, b) => (a.device_name || '').localeCompare(b.device_name || ''),
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Token',
      dataIndex: 'token',
      key: 'token',
      width: 200, // 固定寬度
      ellipsis: true, // 自動省略超出部分
      sorter: (a, b) => a.token.localeCompare(b.token),
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login',
      key: 'last_login',
      width: 200, // 固定寬度
      render: (last_login) =>
        last_login < 2024
          ? '-'
          : new Date(last_login).toLocaleString('zh-TW', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
      sorter: (a, b) => new Date(a.last_login) - new Date(b.last_login),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <span>
          <Popconfirm
            title="Are you sure you want to delete this device?"
            onConfirm={() => handleDeleteDevice(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </span>
      ),
    },
  ];

  const filteredDevices = devices.filter(device =>
    device.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <div className="permissions-container">
      <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
        Back to Permissions
      </Button>
      <h2>用戶設備管理</h2>
      <Table
        dataSource={filteredDevices}
        columns={columns}
        rowKey="id"
        pagination={{
          pageSize: 50,
          hideOnSinglePage: true,
          showSizeChanger: false,
        }}
        loading={loading}
      />
    </div>
  );
};

export default Devices;
